import { ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { RootState } from "@store/store";
import { useGetPermission } from "@hooks/useGetPermission";
import { setCurrentList } from "@store/slices/sessionSlice";
import { PermissionV2 } from "@interfaces/users";

export interface SolicitudesTabConfig {
    title: ReactNode;
    code: string;
    cols: Array<string>;
}

const PYMIO_TAB_CODES = ["MTOD", "MSOL", "MSOLTGT", "MSOLOFER", "MSOLOFACP", "MSOLOFRECH", "MCLI"];
// "MPROC" ("Procesando" in the design prototype — applications currently
// calculating their offer) is v2-only: it isn't part of the legacy page's tab
// set, added here to match the design's 8-tab lineup (see the divergence note
// on `getColumnNames`'s "MPROC" case below).
const NORMAL_TAB_CODES = ["MTOD", "MSOL", "MPROC", "MREV", "MPIN", "MAUT", "MREC", "MREVC", "MCLI", "MINAC"];

/**
 * Ported verbatim from `pages/Solicitudes.tsx` (column-per-tab-per-permission
 * logic) so both the legacy page and the v2 page build identical tab/column
 * sets from the same permission tree — keep any future edits here, not
 * duplicated in either page.
 */
export function useSolicitudesTabsConfig() {
    const dispatch = useDispatch();
    const hasFetched = useRef(false);

    const [configTabs, setConfigTabs] = useState<Array<SolicitudesTabConfig>>([]);

    const currentTab = useSelector((state: RootState) => state.session.filters.current);

    const companiesFilters = useSelector((state: RootState) => state.session.filters.shared_filters.companies);
    const financialFilter = useSelector((state: RootState) => state.session.filters.shared_filters.financials);

    const permission = useGetPermission("MDAS");

    const activeTabIndex = useMemo(() => {
        const found = configTabs.findIndex((t) => t.code === currentTab);
        return found === -1 ? 0 : found;
    }, [currentTab, configTabs]);

    const handleOnChangeTab = async (_: ReactNode, indexTab: number) => {
        dispatch(setCurrentList(configTabs.at(indexTab)?.code ?? ""));
    };

    const getColumnNames = (code: string, forPyMio: boolean = false): Array<string> => {
        const totalCoAssigned = companiesFilters.length;
        let cols: Array<string> = [];
        switch (code) {
            case "MTOD":
                cols = [
                    "id_application_company",
                    "company_name",
                    "company_rfc",
                    "tax_system_type",
                    "user_name",
                    "amount",
                    "term",
                    "screen_time_hours",
                    "total_time_hours",
                    "stage_name",
                    "substage_name",
                    "status_name",
                    "macrochannel",
                    "channel",
                    "source",
                    "assigned_partner",
                    "company_source",
                    "product_description",
                    "comments",
                    "dossier",
                    "interested",
                    "owner",
                    "active",
                    "expire_at",
                ];

                // Index shifted +1 (was 15) by inserting "source" after "channel" above.
                if (totalCoAssigned == 1 && financialFilter.length == 0) {
                    cols.splice(16, 1);
                }

                break;
            case "MSOL":
                if (forPyMio) {
                    cols = [
                        "id_application_company",
                        "company_name",
                        "company_rfc",
                        "user_name",
                        "amount",
                        "term",
                        "total_time_hours",
                        "disburment_date",
                        "macrochannel",
                        "channel",
                        "source",
                        "assigned_partner",
                        "company_source",
                        "product_description",
                        "comments",
                        "dossier",
                    ];

                    // Was `cols.splice(14, 1)` — out of bounds no-op on the original
                    // 14-item array (an existing latent bug); corrected to target
                    // "company_source"'s real index, now shifted by inserting "source".
                    if (totalCoAssigned == 1) {
                        cols.splice(12, 1);
                    }
                } else {
                    cols = [
                        "id_application_company",
                        "company_name",
                        "company_rfc",
                        "tax_system_type",
                        "user_name",
                        "amount",
                        "term",
                        "screen_time_hours",
                        "total_time_hours",
                        "stage_name",
                        "substage_name",
                        "status_name",
                        "macrochannel",
                        "channel",
                        "source",
                        "assigned_partner",
                        "company_source",
                        "product_description",
                        "comments",
                        "dossier",
                        "interested",
                        "owner",
                        "active",
                        "expire_at",
                    ];

                    // Index shifted +1 (was 15) by inserting "source" after "channel" above.
                    if (totalCoAssigned == 1) {
                        cols.splice(16, 1);
                    }
                }
                break;
            // Design-only divergence from the legacy page (see the `NORMAL_TAB_CODES`
            // comment) — "Tiempo en cálculo" is `screen_time_hours` (time in the
            // current status), no `total_time_hours`/lifecycle columns since this
            // tab is specifically the transient "calculating an offer" state.
            case "MPROC":
                cols = [
                    "id_application_company",
                    "company_name",
                    "company_rfc",
                    "tax_system_type",
                    "user_name",
                    "amount",
                    "term",
                    "screen_time_hours",
                    "stage_name",
                    "substage_name",
                    "status_name",
                    "macrochannel",
                    "channel",
                    "source",
                    "assigned_partner",
                    "company_source",
                    "product_description",
                    "comments",
                    "dossier",
                ];

                if (totalCoAssigned == 1) {
                    cols.splice(15, 1);
                }
                break;
            case "MREV":
                cols = [
                    "id_application_company",
                    "company_name",
                    "company_rfc",
                    "tax_system_type",
                    "user_name",
                    "amount",
                    "term",
                    "screen_time_hours",
                    "total_time_hours",
                    // "Score Lendia" only exists on this tab in the design prototype —
                    // no real field backs it (see `score` render in `SolicitudesTable.tsx`).
                    "score",
                    "stage_name",
                    "substage_name",
                    "status_name",
                    "macrochannel",
                    "channel",
                    "source",
                    "assigned_partner",
                    "company_source",
                    "product_description",
                    "comments",
                    "dossier",
                    "active",
                    "expire_at",
                ];

                // Index shifted +2 (was 15) by inserting "score" and "source" above.
                if (totalCoAssigned == 1) {
                    cols.splice(17, 1);
                }
                break;
            case "MPIN":
                cols = [
                    "id_application_company",
                    "company_name",
                    "company_rfc",
                    "tax_system_type",
                    "user_name",
                    "amount",
                    "term",
                    "screen_time_hours",
                    "total_time_hours",
                    // Real-data equivalent of the design's "Días Inactivo" pill — derived
                    // from `last_status_date` (see `SolicitudesTable.tsx`).
                    "days_inactive",
                    "stage_name",
                    "substage_name",
                    "status_name",
                    "macrochannel",
                    "channel",
                    "source",
                    "assigned_partner",
                    "company_source",
                    "product_description",
                    "comments",
                    "dossier",
                    "interested",
                    "owner",
                    "active",
                    "expire_at",
                ];

                // Index shifted +2 (was 15) by inserting "days_inactive" and "source" above.
                if (totalCoAssigned == 1) {
                    cols.splice(17, 1);
                }
                break;
            case "MAUT":
                cols = [
                    "id_application_company",
                    "company_name",
                    "company_rfc",
                    "tax_system_type",
                    "user_name",
                    "amount",
                    "term",
                    // Pipeline expands "Monto/Plazo/Comisión" into separate columns in the
                    // design prototype (its "Tasa" column has no real backing field yet).
                    "opening_fee",
                    "screen_time_hours",
                    "total_time_hours",
                    "stage_name",
                    "substage_name",
                    "status_name",
                    "macrochannel",
                    "channel",
                    "source",
                    "assigned_partner",
                    "company_source",
                    "product_description",
                    "comments",
                    "dossier",
                    "interested",
                    "owner",
                    "active",
                    "expire_at",
                ];

                // Index shifted +2 (was 15) by inserting "opening_fee" and "source" above.
                if (totalCoAssigned == 1) {
                    cols.splice(17, 1);
                }
                break;
            case "MREC":
                cols = [
                    "id_application_company",
                    "company_name",
                    "company_rfc",
                    "tax_system_type",
                    "user_name",
                    "amount",
                    "term",
                    "last_status_date",
                    "total_time_hours",
                    "stage_name",
                    "substage_name",
                    "macrochannel",
                    "channel",
                    "source",
                    "assigned_partner",
                    "company_source",
                    "product_description",
                    "comments",
                    "dossier",
                    "active",
                    "expire_at",
                ];

                // Index shifted +1 (was 14) by inserting "source" after "channel" above.
                if (totalCoAssigned == 1) {
                    cols.splice(15, 1);
                }
                break;
            case "MCLI":
                if (forPyMio) {
                    cols = [
                        "id_application_company",
                        "company_name",
                        "company_rfc",
                        "user_name",
                        "amount",
                        "term",
                        "screen_time_hours",
                        "total_time_hours",
                        "stage_name",
                        "substage_name",
                        "status_name",
                        "macrochannel",
                        "channel",
                        "assigned_partner",
                        "company_source",
                        "comments",
                        "dossier",
                        "interested",
                        "owner",
                    ];
                } else {
                    cols = [
                        "id_application_company",
                        "company_name",
                        "company_rfc",
                        "tax_system_type",
                        "user_name",
                        "amount",
                        "term",
                        "client_total_time_hours",
                        "disburment_date",
                        "macrochannel",
                        "channel",
                        "assigned_partner",
                        "company_source",
                        "product_description",
                        "comments",
                        "dossier",
                        "active",
                        "expire_at",
                    ];

                    if (totalCoAssigned == 1) {
                        cols.splice(12, 1);
                    }
                }
                break;
            case "MREVC":
                cols = [
                    "id_application_company",
                    "company_name",
                    "company_rfc",
                    "tax_system_type",
                    "user_name",
                    "amount",
                    "term",
                    "screen_time_hours",
                    "disburment_date",
                    "stage_name",
                    "substage_name",
                    "status_name",
                    "macrochannel",
                    "channel",
                    "source",
                    "assigned_partner",
                    "company_source",
                    "product_description",
                    "comments",
                    "dossier",
                    "active",
                    "expire_at",
                ];

                // Index shifted +1 (was 15) by inserting "source" after "channel" above.
                if (totalCoAssigned == 1) {
                    cols.splice(16, 1);
                }
                break;
            case "MINAC":
                cols = [
                    "id_application_company",
                    "company_name",
                    "company_rfc",
                    "tax_system_type",
                    "user_name",
                    "amount",
                    "term",
                    "screen_time_hours",
                    "total_time_hours",
                    "stage_name",
                    "substage_name",
                    "status_name",
                    "macrochannel",
                    "channel",
                    "source",
                    "assigned_partner",
                    "company_source",
                    "product_description",
                    "comments",
                    "dossier",
                    "interested",
                    "owner",
                    "expire_at",
                ];

                // Index shifted +1 (was 15) by inserting "source" after "channel" above.
                if (totalCoAssigned == 1) {
                    cols.splice(16, 1);
                }

                if (forPyMio) {
                    cols = [
                        "id_application_company",
                        "company_name",
                        "company_rfc",
                        "user_name",
                        "amount",
                        "term",
                        "total_time_hours",
                        "disburment_date",
                        "macrochannel",
                        "channel",
                        "source",
                        "assigned_partner",
                        "comments",
                        "dossier",
                    ];
                }

                break;
            case "MSOLTGT":
                cols = [
                    "id_application_company",
                    "company_name",
                    "company_rfc",
                    "user_name",
                    "amount",
                    "term",
                    "screen_time_hours",
                    "total_time_hours",
                    "stage_name",
                    "substage_name",
                    "status_name",
                    "macrochannel",
                    "channel",
                    "assigned_partner",
                    "company_source",
                    "product_description",
                    "comments",
                    "dossier",
                    "interested",
                    "owner",
                ];

                if (totalCoAssigned == 1) {
                    cols.splice(14, 1);
                }
                break;
            case "MSOLOFER":
            case "MSOLOFACP":
            case "MSOLOFRECH":
                cols = [
                    "id_application_company",
                    "company_name",
                    "company_rfc",
                    "user_name",
                    "amount",
                    "term",
                    "screen_time_hours",
                    "total_time_hours",
                    "stage_name",
                    "substage_name",
                    "status_name",
                    "macrochannel",
                    "channel",
                    "assigned_partner",
                    "company_source",
                    "comments",
                    "dossier",
                    "interested",
                    "owner",
                ];

                if (totalCoAssigned == 1) {
                    cols.splice(14, 1);
                }
                break;
        }

        return cols;
    };

    const loadInfo = () => {
        if (companiesFilters.length === 0) return;

        let tabs: Array<PermissionV2> = [];
        let forPyMio = false;

        if (companiesFilters[0].name === "PYMIO" && companiesFilters.length === 1) {
            tabs = [...(permission?.children ?? [])]
                .filter((child) => child.can_watch == true)
                .filter((child) => PYMIO_TAB_CODES.includes(child.code))
                .sort((childA, childB) => childA.order - childB.order);

            forPyMio = true;
        } else {
            tabs = [...(permission?.children ?? [])]
                .filter((child) => child.can_watch == true)
                .filter((child) => NORMAL_TAB_CODES.includes(child.code))
                .sort((childA, childB) => childA.order - childB.order);
        }

        setConfigTabs(
            tabs.map((tab) => ({ title: tab.name, code: tab.code, cols: getColumnNames(tab.code, forPyMio) })),
        );
    };

    useEffect(() => {
        loadInfo();
    }, [companiesFilters]);

    useEffect(() => {
        if (hasFetched.current) return;
        loadInfo();

        hasFetched.current = true;
    }, [hasFetched]);

    return { configTabs, activeTabIndex, handleOnChangeTab };
}
